import { useEffect, useMemo } from "react";

import { useFormik } from "formik";

import { useReportsQueriesSelector } from "../../store";
import { GroupBy, ReportsQueries } from "../../store/types";

type FormValues = Partial<ReportsQueries> & {
  firstGroupBy?: string;
  secondGroupBy?: string;
  thirdGroupBy?: string;
  fourthGroupBy?: string;
  fifthGroupBy?: string;
  rowBy?: string;
};

interface IUseGroupByContent {
  fields: Array<keyof ReportsQueries>;
  onChangeDataFormation?: (queries: Partial<ReportsQueries>) => void;
  groupByFieldName?: keyof ReportsQueries;
  defaultQueries?: Partial<ReportsQueries>;
}

export const useGroupByContent = ({
  fields,
  onChangeDataFormation,
  groupByFieldName,
  defaultQueries
}: IUseGroupByContent) => {
  const queries = useReportsQueriesSelector();

  const initialData = fields.map((field) => (defaultQueries ? defaultQueries[field] : queries[field]));

  const initialValues: FormValues = useMemo(() => {
    const values = fields.reduce<FormValues>((result, field) => {
      (result[field] as any) = defaultQueries ? defaultQueries[field] : queries[field];
      return result;
    }, {});

    if (groupByFieldName) {
      const groupByArray = (defaultQueries ? defaultQueries[groupByFieldName] : queries[groupByFieldName]) as
        | GroupBy
        | undefined;

      values.firstGroupBy = groupByArray?.[0]?.[0];
      values.secondGroupBy = groupByArray?.[1]?.[0];
      values.thirdGroupBy = groupByArray?.[2]?.[0];
      values.fourthGroupBy = groupByArray?.[3]?.[0];
      values.fifthGroupBy = groupByArray?.[4]?.[0];
    }

    // Initialize rowBy from defaultQueries or fallback to queries
    values.rowBy = defaultQueries?.rowBy || queries.rowBy;

    return values;
  }, [...initialData]);

  const { setFieldValue, values } = useFormik<FormValues>({
    initialValues,
    onSubmit: () => {},
    enableReinitialize: true
  });

  useEffect(() => {
    const { firstGroupBy, secondGroupBy, thirdGroupBy, fourthGroupBy, fifthGroupBy, rowBy, ...rest } = values;
    let data: Partial<ReportsQueries> = rest;

    if (groupByFieldName) {
      const newGroupBy: GroupBy = [];

      if (firstGroupBy?.length) {
        newGroupBy.push([firstGroupBy]);
      }

      if (secondGroupBy?.length) {
        newGroupBy.push([secondGroupBy]);
      }

      if (thirdGroupBy?.length) {
        newGroupBy.push([thirdGroupBy]);
      }

      if (fourthGroupBy?.length) {
        newGroupBy.push([fourthGroupBy]);
      }

      if (fifthGroupBy?.length) {
        newGroupBy.push([fifthGroupBy]);
      }

      data = {
        ...rest,
        [groupByFieldName]: newGroupBy
      };
    }

    // Include rowBy in the data if it's defined
    if (rowBy) {
      data = {
        ...data,
        rowBy
      };
    }

    onChangeDataFormation?.(data);
  }, [values]);

  return {
    values,
    setFieldValue
  };
};
